import { isEqual } from 'lodash';
import { FormMode, SelectItemType, SelectItemTypeObject, ValueComparator } from './types';
import { truthy } from '@/utils/helper';
import { v7 as uuidv7 } from 'uuid';
import { computed, inject } from 'vue';
import { injectStrict } from '@/utils/injectStrict';
import { formModeInjectionKey } from './useVeeForm';

export const EMPTY_FIELD_SIGN = '—';

/**
 * Returns EMPTY_FIELD_SIGN if the value is falsey, otherwise returns the value as string
 */
export function displayValue(value: any): string {
  if (!value) {
    return EMPTY_FIELD_SIGN;
  }
  return String(value);
}

export function getPrefixedUuid(prefix: string = 'input') {
  return `${prefix}-${uuidv7()}`;
}

export function isSelectItemTypeObject<Value>(
  value: SelectItemType<Value>,
): value is SelectItemTypeObject<Value> {
  return value && typeof value === 'object' && 'value' in value;
}

/**
 * Return all the titles for the selection of a selection control
 */
export function getItemsForSelection<Value>(
  options?: SelectItemType<Value>[],
  selectedValue?: Value | Value[],
  valueComparator?: ValueComparator,
) {
  if (options?.length) {
    const normalizedOptions = [...(options instanceof Array ? options : [options])].map((option) =>
      isSelectItemTypeObject(option) ? option : { title: String(option), value: option },
    );

    return [...(selectedValue instanceof Array ? selectedValue : [selectedValue])]
      .map((value) =>
        normalizedOptions.find((item) => {
          if (valueComparator && value) {
            return valueComparator(item.value, value);
          } else {
            return isEqual(item.value, value);
          }
        }),
      )
      .filter(truthy);
  } else {
    return undefined;
  }
}

function isPartialPath(path: string, partialPath: string) {
  // escape symbols on the partial that are reserved in regexes to also match array access "[$index]"
  const escapedPartialPath = partialPath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // check that the partialPath is a section, substring ending before "." or "[#]"
  return new RegExp(`^${escapedPartialPath}(\\[(\\d+)\\])?(\\..+|$)`).test(path);
}

export function hasVeeValidationErrorsForPaths(
  errors: Partial<Record<string, string>> | undefined = {},
  paths: string[] | undefined = [],
) {
  return Object.entries(errors).some(([path, errorMessage]) => {
    const match = paths.some((partialPath) => isPartialPath(path, partialPath));
    return match && errorMessage;
  });
}

export function useVeeFormProps<T extends { disabled?: boolean; mode?: FormMode }>(props: T) {
  // Vuetify propagates a form-wide `disabled` state from <v-form> to its *input* components
  // via provide/inject (the VForm context). As soon as you wrap a Vuetify input (e.g. <v-text-field>)
  // inside your own component (e.g. <MyTextField>) and introduce a local `disabled` prop,
  // you break that automatic inheritance — your wrapper is *not* a VField/VInput, so it won’t
  // receive the form context by default.
  const formCtx = inject<any>(Symbol.for('vuetify:form'), null);

  const formMode = injectStrict(formModeInjectionKey);

  return computed(() => {
    const formDisabled = formCtx?.isDisabled?.value ?? false;
    return {
      ...props,
      disabled: !!(props.disabled || formDisabled),
      mode: props?.mode ?? formMode.value,
    };
  });
}
